import { styled } from '@defa/defa-component-library';

/**
 * The brand column's width on wide screens — the same 360px the library's
 * `SplitScreen` used, so the desktop layout is unchanged.
 */
const BRAND_COLUMN_PX = 360;

/** The widest the content column gets. Forms stop being readable past this. */
const CONTENT_MAX_PX = 480;

/**
 * Where the two columns stop fitting: the brand column, the content column and
 * a gutter either side. A layout decision, not a device class — which is why it
 * lives here and not in the component library.
 */
const STACK_BREAKPOINT_PX = BRAND_COLUMN_PX + CONTENT_MAX_PX + 64;

const stacked = `@media (max-width: ${STACK_BREAKPOINT_PX}px)`;

export const Root = styled.div`
    display: flex;
    flex: 1;
    align-items: stretch;
    min-height: 100%;
    background-color: ${({ theme }) => theme.secondSideBackgroundColor};

    ${stacked} {
        flex-direction: column;
    }
`;

export const Brand = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 ${BRAND_COLUMN_PX}px;
    max-width: ${BRAND_COLUMN_PX}px;
    padding: ${({ theme }) => `${theme.spacing(12)} ${theme.spacing(6)}`};
    background-color: ${({ theme }) => theme.firstSideBackgroundColor};

    ${stacked} {
        /* A header strip rather than a column: full width, only as tall as its
           content, and laid out horizontally so the mark and any strapline sit
           on one line above the form. */
        flex: 0 0 auto;
        max-width: none;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        padding: ${({ theme }) => theme.spacing(4)};

        /* See ContentColumn: a Group passed as the brand brings its own 48px
           side margins, which would inset the header strip twice over. */
        & > [data-group] {
            margin: 0;
        }

        /* Group also puts a 52px gap under each of its children, to separate
           them in a tall column. In a header strip that reads as dead space —
           and doubly so under a WideOnly sibling that is display:none here, so
           there is nothing left to separate from. */
        & > [data-group] > :not([data-group]) {
            margin-bottom: 0;
        }
    }
`;

export const Content = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    /* Scrolls independently of the brand column on wide screens; on a phone the
       whole page scrolls instead, so the header can leave the viewport. */
    overflow: auto;

    ${stacked} {
        overflow: visible;
        padding: 0 ${({ theme }) => theme.spacing(4)};
    }
`;

export const ContentColumn = styled.div`
    display: flex;
    flex-direction: column;
    /* min(), not a min-width: a fixed minimum is what made every auth page
       overflow horizontally below ~936px. */
    width: min(${CONTENT_MAX_PX}px, 100%);

    /* A Group carries 48px side margins of its own. Left alone they would inset
       the content inside this column — narrowing the fields to ~380px on a
       desktop, where they used to be the full 480px, and to ~250px on a phone.
       The column's width and the gutter above are the layout here, so drop
       them. Group marks its wrapper with data-group and uses the same selector
       to manage its children's spacing. */
    & > [data-group] {
        margin-left: 0;
        margin-right: 0;
    }
`;

/**
 * Hides its children once the layout stacks. For brand-side extras that earn
 * their place in a 360px column but only crowd the header strip on a phone.
 */
export const WideOnly = styled.div`
    ${stacked} {
        display: none;
    }
`;
